// Server-derived rechecks -> the view models the recheck surfaces render.
//
// bead hf-e6-commitments-coverage-udt.5.
//
// ── WHAT THIS MODULE DOES NOT DECIDE ───────────────────────────────────────────
//
// It does not decide what changed. `convex/domain/commitments.ts:myRechecks` ran the
// kernel's `materialDifferences` over the revision the person agreed to and the one
// they are being shown; this turns those facts into local-time labels. A browser that
// re-derived the diff would be a second answer to "what am I agreeing to?", and the
// two would disagree the first time a revision row was read differently.
//
// It also does not decide whether a change was material. That verdict belongs to
// `classifyNeedChange` in the kernel, ran at the moment of the edit — by the time a
// recheck exists, the question has already been settled.

import { instant, timeZone, type TimeZoneId } from "@herefor/domain-kernel";
import type { commitments as contracts } from "@herefor/contracts";
import { NEED_SURFACE_MESSAGES, RECHECK_SURFACE_MESSAGES as COPY } from "@herefor/product-language";

import { formatWindowInZone } from "../plan/planData";

/**
 * One difference, ready to render as a "Was" / "Now" pair.
 *
 * `was` is null for a change with no previous value worth showing — an appointment
 * that had no time before, or a location whose only stored form is a fingerprint.
 * The surface renders the explanation instead of an empty cell, because a blank
 * beside "Was" reads as data loss rather than as "there wasn't one".
 */
export interface DifferenceView {
  readonly key: string;
  /** What moved, named so the reader knows before comparing two timestamps. */
  readonly label: string;
  readonly was: string | null;
  readonly now: string | null;
  /** Used instead of the Was/Now pair when there are no values to show. */
  readonly explanation: string | null;
}

export interface RecheckView {
  readonly commitmentId: string;
  readonly supportNeedId: string;
  readonly label: string;
  readonly href: string | null;
  /** The need's CURRENT window — what they are being asked about (NED-002). */
  readonly windowLabel: string;
  readonly needRevision: number;
  readonly differences: readonly DifferenceView[];
}

export interface RecheckContext {
  readonly timeZone: string;
  /** Builds `/c/<ref>/needs/<id>`. Absent when the surface has no route to offer. */
  readonly publicPlanRef?: string;
}

export function toRecheckViews(
  rechecks: readonly contracts.PendingRecheckView[],
  context: RecheckContext,
): readonly RecheckView[] {
  const zone = timeZone(context.timeZone);

  return rechecks.map((recheck) => ({
    commitmentId: recheck.commitmentId,
    supportNeedId: recheck.supportNeedId,
    label: recheck.label,
    href:
      context.publicPlanRef === undefined
        ? null
        : `/c/${context.publicPlanRef}/needs/${recheck.supportNeedId}`,
    windowLabel: windowLabel(recheck.window, zone),
    needRevision: recheck.needRevision,
    differences: recheck.differences.map((difference, index) =>
      toDifferenceView(difference, index, zone),
    ),
  }));
}

/**
 * One difference, in the circle's own words and the circle's own timezone.
 *
 * Exhaustive over the wire union, with no default branch: a new kind of material
 * change must not be able to render as a blank row. TypeScript enforces it through
 * the `never` at the end.
 */
export function toDifferenceView(
  difference: contracts.RevisionDifference,
  index: number,
  zone: TimeZoneId,
): DifferenceView {
  const key = `${difference.field}-${String(index)}`;

  switch (difference.field) {
    case "needWindow":
      return {
        key,
        label: COPY.changed.needWindow,
        was: windowLabel(difference.was, zone),
        now: windowLabel(difference.now, zone),
        explanation: null,
      };
    case "requiredCapacity":
      return {
        key,
        label: COPY.changed.requiredCapacity,
        // A COUNT of people, never a fraction and never a bar (COV-006, NED-012).
        was: COPY.peopleNeeded(difference.was),
        now: COPY.peopleNeeded(difference.now),
        explanation: null,
      };
    case "requiredHelpModes":
      return {
        key,
        label: COPY.changed.requiredHelpModes,
        was: modeList(difference.was),
        now: modeList(difference.now),
        explanation: null,
      };
    case "eventWindow":
      return {
        key,
        label: COPY.changed.eventWindow,
        // Null on either side is an appointment that had, or has, no time yet. The
        // surface says so rather than printing an empty cell that reads as a bug.
        was: difference.was === null ? null : windowLabel(difference.was, zone),
        now: difference.now === null ? null : windowLabel(difference.now, zone),
        explanation: null,
      };
    case "eventLocation":
      // No values at all, on purpose: the revision row holds a fingerprint rather
      // than an address (RULE 2), so the change is named and the detail is on the
      // plan. A hash on this screen would be worse than saying nothing.
      return {
        key,
        label: COPY.changed.eventLocation,
        was: null,
        now: null,
        explanation: COPY.locationMoved,
      };
    default: {
      const unreachable: never = difference;
      return unreachable;
    }
  }
}

/** True when the server could not read back what the person originally agreed to. */
export function hasNoReadableChanges(recheck: RecheckView): boolean {
  return recheck.differences.length === 0;
}

function windowLabel(window: contracts.WireWindow, zone: TimeZoneId): string {
  return formatWindowInZone(zone, instant(window.startsAt), instant(window.endsAt));
}

/**
 * Help modes as words, in the order the product lists them everywhere else.
 *
 * Reordering is not a change (the kernel compares them as a set), so the display
 * order is fixed rather than taken from the payload — otherwise two identical sets
 * could read as different lists.
 */
function modeList(modes: readonly string[]): string {
  const known = ["in_person", "driving", "phone_or_online"] as const;
  return known
    .filter((mode) => modes.includes(mode))
    .map((mode) => NEED_SURFACE_MESSAGES.helpModes[mode])
    .join(" · ");
}
